import Sentry from '@/sentry'
import { proxy, useSnapshot } from 'valtio'
import { devtools } from 'valtio/utils'

import { clearStore } from '@/lib/valtio/reset-store'

import { parseCueSnapshot } from '~shared/rundown/cue'
import type { Cue } from '~shared/rundown/cue'

import type { DocumentChange } from 'firebase/firestore'

type Cues = Record<Cue['id'], Cue>

/**
 * Store for the `rundown/cues` subcollection.
 *
 * Holds only the cue _documents_, keyed by id and unordered. Order lives on
 * `rundown.cues` (a tree of ids), and everything ordered or indexed is derived
 * from the two in `cue.derived.ts`.
 *
 * @remarks
 *   Should be treated as read-only, mutations happen through the API. That is
 *   why it is not exported, so it can only be modified inside this module. A
 *   read-only reference is exported for derived data.
 *
 * @warning The Firestore listener must stay the ONLY writer. Never write an API
 * response back in as an optimistic update. That is a second writer racing the
 * listener, and an older snapshot arriving after a newer PATCH response silently
 * reverts the user's edit.
 */
const cuesStore = proxy<Cues>({})

export const cuesStoreActions = {
  /**
   * Syncs the local store with the snapshot from the Firestore listener.
   *
   * The listener query filters on `deletedAt == null`, so a soft-deleted cue
   * leaves the result set and arrives here as a `removed` change.
   */
  processDocChanges(changes: DocumentChange[]) {
    changes.forEach((change) => {
      // Delta: removed
      //
      // The removed document needs no parsing: its id is all the store needs to
      // drop it, and its body may already be gone.
      if (change.type === 'removed') {
        delete cuesStore[change.doc.id]
        return
      }

      // Delta: added | modified
      //
      // A single malformed document must not tear down the live session, so a
      // schema failure is reported and skipped rather than thrown.
      let cue: Cue
      try {
        cue = parseCueSnapshot(change.doc)
      } catch (error) {
        Sentry.captureException(error)
        return
      }

      const existing = cuesStore[cue.id]
      if (existing) {
        // Object.assign does the merge in place, so the proxy identity stays
        // stable and only the changed fields notify subscribers.
        // Note: If cue documents can have fields removed (not just nulled),
        // then we probably need a small helper that can sync the stored cue's
        // fields and deletes fields that were removed in the document.
        Object.assign(existing, cue)
      } else {
        cuesStore[cue.id] = cue
      }
    })
  },

  /**
   * Empties the store. Called when leaving a rundown.
   */
  resetStore() {
    clearStore(cuesStore)
  },
}

/**
 * Look up a cue in `cuesStore` directly.
 *
 * @remarks
 *   Reading through the proxy (not a snapshot) is deliberate: this is what the
 *   derived layer calls, and valtio only tracks reads that go through the
 *   proxy.
 *
 * @returns undefined if the cue document has not arrived or was deleted
 */
export function getCue(cueId: string): Cue | undefined {
  return cuesStore[cueId]
}

/**
 * The cue store itself, for the derived layer and the store hooks.
 *
 * @remarks
 *   Exported as read-only to keep the single-writer rule enforceable by the
 *   type system: subscribing and reading is fine, assigning is not.
 */
export const cuesStoreReadonly: Readonly<Cues> = cuesStore

// =============== HOOKS

/**
 * Subscribe to a single cue.
 *
 * @returns undefined if the cue document has not arrived or was deleted
 */
export function useCueSnapshot(cueId: string): Cue | undefined {
  const snap = useSnapshot(cuesStore)

  return snap[cueId]
}

/**
 * Subscribe to every cue, keyed by id.
 *
 * Use in places that need to look up many cues by id (eg. output views walking
 * an indexed list). When rendering a single row use {@link `useCueSnapshot`}.
 *
 * @remarks
 *   Unordered and unfiltered. This is the raw collection, so it can contain a
 *   cue that no longer has a place in the order (`rundown.cues`). Iterate
 *   `cuesDerived` when order or membership matters, and use this only to
 *   resolve ids you already got from there.
 */
export function useCuesById(): Readonly<Record<string, Cue>> {
  return useSnapshot(cuesStore)
}

// #region ============== DEV
if (process.env.NODE_ENV !== 'production') {
  devtools(cuesStore, { name: 'cuesStore', enabled: true })
}
// #endregion
